/**
 * Color utilities for Remotion video templates
 *
 * Provides color manipulation functions used across templates including
 * hex parsing, brightness adjustment, and format conversion.
 */

/**
 * RGB color representation
 */
export interface RGB {
  r: number;
  g: number;
  b: number;
}

/**
 * Converts a hex color string to RGB values.
 *
 * Handles both 3-character (#RGB) and 6-character (#RRGGBB) hex formats.
 * Returns black (0, 0, 0) for invalid input.
 *
 * @param hex - Hex color string (e.g., '#1a1a2e' or '#fff')
 * @returns Object containing r, g, b values (0-255)
 *
 * @example
 * hexToRgb('#1a1a2e') // { r: 26, g: 26, b: 46 }
 * hexToRgb('#fff')    // { r: 255, g: 255, b: 255 }
 */
export function hexToRgb(hex: string): RGB {
  // Remove # if present
  const cleanHex = hex.replace('#', '');

  // Handle 3-character shorthand (#RGB -> #RRGGBB)
  const fullHex =
    cleanHex.length === 3
      ? cleanHex
          .split('')
          .map((c) => c + c)
          .join('')
      : cleanHex;

  const result = /^([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(fullHex);

  if (!result) {
    return { r: 0, g: 0, b: 0 };
  }

  return {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16),
  };
}

/**
 * Converts a hex color to an RGBA string.
 *
 * @param hex - Hex color string (e.g., '#1a1a2e')
 * @param alpha - Alpha/opacity value (0-1)
 * @returns CSS rgba() string
 *
 * @example
 * hexToRgba('#1a1a2e', 0.5) // 'rgba(26, 26, 46, 0.5)'
 */
export function hexToRgba(hex: string, alpha: number): string {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Adjusts the brightness of a hex color by a percentage.
 *
 * Positive values lighten the color, negative values darken it.
 * The function clamps RGB values to the valid 0-255 range.
 *
 * @param hex - Hex color string (e.g., '#1a1a2e')
 * @param percent - Percentage to adjust (-100 to 100)
 * @returns Adjusted hex color string
 *
 * @example
 * adjustBrightness('#1a1a2e', 40)  // Lighten by 40%
 * adjustBrightness('#ffffff', -20) // Darken by 20%
 */
export function adjustBrightness(hex: string, percent: number): string {
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = Math.max(0, Math.min(255, (num >> 16) + amt));
  const G = Math.max(0, Math.min(255, ((num >> 8) & 0x00ff) + amt));
  const B = Math.max(0, Math.min(255, (num & 0x0000ff) + amt));
  return `#${(0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)}`;
}

/**
 * Shifts the brightness of a color by a fixed amount.
 *
 * Unlike adjustBrightness (which uses percentage), this adds/subtracts
 * a direct value to each RGB component.
 *
 * @param hex - Hex color string (e.g., '#1a1a2e')
 * @param amount - Amount to shift each channel (-255 to 255)
 * @returns Shifted hex color string
 *
 * @example
 * shiftColor('#808080', 20)  // Lighten by 20 units
 * shiftColor('#ffffff', -50) // Darken by 50 units
 *
 * @deprecated Use adjustBrightness for percentage-based adjustment.
 *             This function is kept for backwards compatibility.
 */
export function shiftColor(hex: string, amount: number): string {
  const { r, g, b } = hexToRgb(hex);
  const newR = Math.min(255, Math.max(0, r + amount));
  const newG = Math.min(255, Math.max(0, g + amount));
  const newB = Math.min(255, Math.max(0, b + amount));
  return `#${((1 << 24) | (newR << 16) | (newG << 8) | newB).toString(16).slice(1)}`;
}

/**
 * Converts RGB values to a hex color string.
 *
 * @param r - Red value (0-255)
 * @param g - Green value (0-255)
 * @param b - Blue value (0-255)
 * @returns Hex color string (e.g., '#1a1a2e')
 *
 * @example
 * rgbToHex(26, 26, 46) // '#1a1a2e'
 */
export function rgbToHex(r: number, g: number, b: number): string {
  const clamp = (v: number): number => Math.max(0, Math.min(255, Math.round(v)));
  return `#${((1 << 24) | (clamp(r) << 16) | (clamp(g) << 8) | clamp(b)).toString(16).slice(1)}`;
}

/**
 * Blends two colors together.
 *
 * @param color1 - First hex color
 * @param color2 - Second hex color
 * @param ratio - Blend ratio (0 = color1, 1 = color2)
 * @returns Blended hex color string
 *
 * @example
 * blendColors('#000000', '#ffffff', 0.5) // '#808080' (gray)
 */
export function blendColors(color1: string, color2: string, ratio: number): string {
  const rgb1 = hexToRgb(color1);
  const rgb2 = hexToRgb(color2);

  const r = Math.round(rgb1.r + (rgb2.r - rgb1.r) * ratio);
  const g = Math.round(rgb1.g + (rgb2.g - rgb1.g) * ratio);
  const b = Math.round(rgb1.b + (rgb2.b - rgb1.b) * ratio);

  return rgbToHex(r, g, b);
}
